import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Lock } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { validateGateFormula, gatePassDetail } from '@/lib/gateFormulaValidation'
import type { FormulaGate } from '@/types/cgt'
import { FormFieldRefSelect, type RefFormGroup } from './FormFieldRefSelect'

interface Props {
  label: string
  helperText: string
  gate: FormulaGate | undefined
  onChange: (gate: FormulaGate | undefined) => void
  /** Field-picker groups, e.g. [{ key: '', name: 'This form', fields: [...] }]. */
  refGroups: RefFormGroup[]
  labelById: Record<string, string>
  /**
   * What stays blocked when this gate fails, e.g. 'this field stays locked'.
   * Shown only in a fail state — a sound gate does not advertise the
   * consequence of a broken one.
   */
  blockedConsequence: string
}

/**
 * Editor for a formula-based gate (unlock / signature / whole-form
 * completion). Unlike FormulaBuilder's fully clickable calculated-field
 * formula, this is a raw-text formula input — a Boolean formula gate is an
 * "advanced escape hatch" alongside the simpler structured mechanisms
 * (VisibilityBuilder, unlock-after MultiSelect) already offered for these
 * same slots, so a text field with field-ref insertion + a live human-
 * readable summary matches its "power user override" role.
 *
 * The verdict below the Formula box is computed from what is typed, on every
 * render, by `validateGateFormula` — so it also re-checks a stored gate
 * against the current form definition each time the panel is opened. The
 * fail-closed rule is help text (it is policy, true of every gate) and its
 * consequence appears only when this particular gate is actually broken.
 */
export function GateFormulaEditor({
  label,
  helperText,
  gate,
  onChange,
  refGroups,
  labelById,
  blockedConsequence,
}: Props) {
  const [pick, setPick] = useState('')
  const enabled = gate !== undefined
  const formula = gate?.formula ?? ''
  const validation = validateGateFormula(formula, labelById)

  return (
    <div className='space-y-1.5'>
      <div className='flex items-center justify-between'>
        <Label className='flex items-center gap-1.5 font-normal'>
          <Lock className='h-3.5 w-3.5' />
          {label}
        </Label>
        <Switch
          checked={enabled}
          onCheckedChange={v => onChange(v ? { formula: '' } : undefined)}
        />
      </div>
      <p className='text-xs text-muted-foreground'>{helperText}</p>

      {enabled && (
        <div className='space-y-2 rounded-md border bg-muted/20 p-2.5'>
          <div className='space-y-1'>
            <Label className='text-xs'>Formula</Label>
            <Textarea
              value={formula}
              onChange={e => onChange({ ...gate, formula: e.target.value })}
              placeholder='e.g. ALL({days_since_draw} &lt;= 7, TODAY() &lt;= {expiration})'
              className='min-h-16 font-mono text-xs'
            />
            <div className='flex items-center gap-1.5'>
              <FormFieldRefSelect
                groups={refGroups}
                value={pick}
                onChange={refKey => {
                  onChange({ ...gate, formula: `${formula}${formula ? ' ' : ''}{${refKey}}` })
                  setPick('')
                }}
                formPlaceholder='Pick a form…'
                fieldPlaceholder='Insert a field…'
                suggestFor={{ formula }}
              />
            </div>

            {validation.state === 'empty' && (
              <p className='text-[11px] text-muted-foreground'>
                Gates fail closed: while the formula is blank, a referenced field is
                missing, or the formula is invalid, {blockedConsequence}.
              </p>
            )}

            {validation.state === 'fail' && (
              <div className='flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs text-destructive'>
                <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                <span>
                  <span className='font-medium'>{validation.message}</span> Because a gate
                  fails closed, {blockedConsequence} until this is fixed
                  {validation.kind === 'unknown-ref' ? (
                    <>
                      {' '}
                      — use <span className='font-medium'>Insert a field</span> above, or
                      copy the real ID from the field&apos;s Properties panel.
                    </>
                  ) : (
                    '.'
                  )}
                </span>
              </div>
            )}

            {validation.state === 'pass' && (
              <div className='flex items-start gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-2.5 py-2 text-xs text-emerald-700 dark:text-emerald-400'>
                <CheckCircle2 className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                <span>
                  <span className='font-medium'>Valid.</span>{' '}
                  {gatePassDetail(validation, formula, labelById)}
                  {validation.summary && (
                    <>
                      <br />
                      <span className='font-medium'>Reads as:</span> {validation.summary}
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
          <div className='space-y-1'>
            <Label className='text-xs'>Message when blocked</Label>
            <Input
              value={gate?.message ?? ''}
              onChange={e => onChange({ ...gate, message: e.target.value })}
              placeholder='e.g. The IDM results have expired. A new IDM draw is required.'
              className='h-8 text-sm'
            />
          </div>
        </div>
      )}
    </div>
  )
}
